/* eslint-disable no-throw-literal */
import { useLingui } from "@lingui/react";
import { useMemo } from "react";
import { useIsSinglePerson } from "../hooks/useIsSinglePerson";
import { i18n } from "@lingui/core";
import { t } from "@lingui/macro";
import { Gewest } from "../personaliaForm/citiesApi";
import { useFormikContext } from "formik";
import { evolve, path, replace } from "ramda";
import { AJR, IJR } from "../../../settings";
import { flow, identity, pipe } from "fp-ts/lib/function";
import { either } from "fp-ts";

const codesVoorafBetalingen = ["1570", "1571", "1572", "1573", "1574", "2570", "2571", "2572", "2573", "2574"];
export interface TaxCode {
  code: string;
  name: string;
  default: string;
  type: "" | "bool" | "num" | "date" | "pct";
  mar: "" | "1";
  mutExclWith: number[];
}

export interface TaxCodes {
  [code: string]: Omit<TaxCode, "code">;
}

export const getTaxCodesForGewest = (gewest?: Gewest) => {
  if (!gewest) {
    return either.left({ error: i18n._(t`Postcode niet ingevuld`) as string });
  }
  return pipe(require(`./taxCodes_${gewest}.json`) as TaxCodes, either.right);
};

const getTaxCodeDetails = (taxCode: string) => (taxCodes: TaxCodes) => {
  const taxCodeDetails = taxCodes[taxCode] as TaxCode | undefined;

  if (!taxCodeDetails || codesVoorafBetalingen.includes(taxCode)) {
    return either.left({ error: i18n._(t`Ongeldige code`) as string });
  }

  return either.right({ ...taxCodeDetails, code: taxCode });
};

export const getTaxCodeDetailsForGewest = (gewest: Gewest) => (code: string) =>
  pipe(gewest, getTaxCodesForGewest, either.chain(getTaxCodeDetails(code)));

const extractGewestNameForLocale = (locale: string) => evolve({ name: path([locale]) });

const replaceJaartallen = evolve({
  name: flow(
    replace("{AJR}", AJR.toString()),
    replace("{IJR}", IJR.toString()),
    replace("{IJR-1}", (IJR - 1).toString()),
    replace("{IJR-2}", (IJR - 2).toString()),
    replace("{IJR-9}", (IJR - 9).toString()),
    replace("{IJR-12}", (IJR - 12).toString())
  ),
});

export const isPartnerCode = (taxCode: TaxCode) =>
  taxCode.code.startsWith("2") || taxCode.code.startsWith("4") || taxCode.mar === "1";

const validatePartnerCode = (isSinglePerson: boolean) => <T extends TaxCode>(taxCodeDetails: T) => {
  if (isPartnerCode(taxCodeDetails) && isSinglePerson) {
    return either.left({ error: i18n._(t`Deze code is niet toegelaten voor alleenstaanden`) as string });
  }
  return either.right(taxCodeDetails);
};

export const useTaxCode = (taxCode: string) => {
  const {
    values: { gewest },
  } = useFormikContext();
  const {
    i18n: { locale },
  } = useLingui();
  const isSinglePerson = useIsSinglePerson();

  return useMemo<{ taxCode?: TaxCode; error?: string }>(() => {
    return pipe(
      gewest,
      getTaxCodesForGewest,
      either.chain(getTaxCodeDetails(taxCode)),
      either.chain(validatePartnerCode(isSinglePerson)),
      either.map(extractGewestNameForLocale(locale)),
      either.map(replaceJaartallen),
      either.foldW(identity, (taxCode) => ({ taxCode }))
    );
  }, [isSinglePerson, locale, taxCode, gewest]);
};
